/**
 * Filtros en borrador y aplicados (QA-2 de la feature 005).
 *
 * Las listas del Admin ya no piden datos al cambiar un filtro: el admin arma los filtros en un
 * **borrador** y los aplica con "Buscar" (o Enter dentro de un campo). La consulta de TanStack
 * Query usa solo los **aplicados**; mientras el borrador difiera, `dirty` permite resaltar "Buscar".
 *
 * La comparación trata como equivalentes los valores "vacíos" (`undefined`, `null`, `''`, `false`)
 * y recorta espacios en los textos: borrar un filtro o escribir un espacio no cuenta como cambio.
 */
import { useCallback, useState } from 'react';

type FilterValue = unknown;

function normalizeValue(value: FilterValue): FilterValue {
  if (value === undefined || value === null || value === false) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return value;
}

/** `true` si los dos conjuntos de filtros piden lo mismo a la API. */
export function sameFilters<T extends object>(a: T, b: T): boolean {
  const left = a as Record<string, FilterValue>;
  const right = b as Record<string, FilterValue>;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (normalizeValue(left[key]) !== normalizeValue(right[key])) {
      return false;
    }
  }
  return true;
}

export interface AppliedFilters<T extends object> {
  /** Lo que el admin está eligiendo en la barra de filtros. */
  draft: T;
  /** Lo que la consulta usa (y lo que decide "sin resultados" frente a "vacío"). */
  applied: T;
  /** Cambia una parte del borrador sin pedir datos. */
  update: (patch: Partial<T>) => void;
  /** Aplica el borrador ("Buscar" o Enter). */
  apply: () => void;
  /** Vuelve a los valores por defecto y los aplica ("Limpiar filtros"). */
  clear: () => void;
  /** El borrador difiere de lo aplicado: hay cambios sin buscar. */
  dirty: boolean;
  /** Lo aplicado difiere de los valores por defecto. */
  isFiltered: boolean;
  /** Borrador o aplicado difieren de los valores por defecto (mostrar "Limpiar filtros"). */
  canClear: boolean;
}

/**
 * @param defaults valores por defecto (los que aplica "Limpiar filtros").
 * @param initial valores de arranque, si difieren de los por defecto (p. ej. `?site_id=` de la URL).
 */
export function useAppliedFilters<T extends object>(defaults: T, initial?: T): AppliedFilters<T> {
  const [defaultValues] = useState<T>(defaults);
  const [draft, setDraft] = useState<T>(initial ?? defaults);
  const [applied, setApplied] = useState<T>(initial ?? defaults);

  const update = useCallback((patch: Partial<T>) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  const apply = useCallback(() => {
    // Sin cambios reales se conserva la misma referencia: la clave de la query no cambia.
    setApplied((current) => (sameFilters(current, draft) ? current : draft));
  }, [draft]);

  const clear = useCallback(() => {
    setDraft(defaultValues);
    setApplied(defaultValues);
  }, [defaultValues]);

  const dirty = !sameFilters(draft, applied);
  const isFiltered = !sameFilters(applied, defaultValues);
  const canClear = isFiltered || !sameFilters(draft, defaultValues);

  return { draft, applied, update, apply, clear, dirty, isFiltered, canClear };
}
